import { Link } from "react-router-dom";
import { LIBRARY_IMAGE, OAK_IMAGE, ABOUT_HERO_IMAGE, ATTORNEYS, TEAM_PORTRAITS, FOUNDER_MANIFESTO, FOUNDER_VALUES, PROFESSIONAL_MEMBERSHIPS, SPEAKING_PUBLISHED, PRESS_MEDIA } from "@/lib/content";
import SEO from "@/components/SEO";
import StarConstellation from "@/components/StarConstellation";
import PressStrip from "@/components/PressStrip";
import OfficeGallery from "@/components/OfficeGallery";
import { founderPersonSchema, breadcrumbSchema } from "@/lib/schemas";
import AttorneyReviewNote from "@/components/AttorneyReviewNote";

const PRINCIPLES = [
  {
    no: "I",
    title: "Patience",
    body: "The best legal work compounds. We rarely give the urgent answer; we always give the lasting one.",
  },
  {
    no: "II",
    title: "Discretion",
    body: "Most of our matters are never reported, never publicized, and never need to be. That is the work.",
  },
  {
    no: "III",
    title: "Craft",
    body: "We draft documents as a cabinetmaker joins wood — measured, tested, fit to last beyond the maker.",
  },
  {
    no: "IV",
    title: "Continuity",
    body: "Your counsel does not rotate. The lawyer who drafts your shareholder agreement will counsel your heirs.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page" className="bg-ivory">
      <SEO
        title="Our Approach — A Texas Counsel Measured in Decades"
        description="Founded by Darryl V. Pratt, attorney and CPA, Lone Star Counsel advises Texas business owners, physicians, franchise investors, and Indian and South Asian families and business owners across the business, wealth and legacy continuum."
        path="/about"
        schema={[
          founderPersonSchema(),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Our Approach", path: "/about" },
          ]),
        ]}
      />
      {/* hero */}
      <section className="relative panel-navy min-h-[70vh] flex items-end" data-testid="about-hero">
        <div className="absolute inset-0">
          <img src={ABOUT_HERO_IMAGE} alt="Lone Star Counsel Texas ranch estate at golden hour" className="w-full h-full object-cover opacity-55" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-navy/60 via-navy/40 to-navy/95"></div>
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 py-32 md:py-40 w-full">
          <div className="kicker kicker-ivory">Our Approach</div>
          <div className="mt-3 text-[11px] uppercase tracking-[0.42em] text-brass font-medium" data-testid="about-disciplines">
            Attorney <span className="text-brass/50 mx-2">·</span> CPA <span className="text-brass/50 mx-2">·</span> Business Advisor
          </div>
          <h1 className="mt-8 font-display text-ivory text-6xl md:text-8xl leading-[0.95] tracking-tight max-w-5xl">
            A counsel <br/>
            <span className="italic font-cormorant text-brass">measured in decades.</span>
          </h1>
        </div>
      </section>

      {/* Philosophy */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-32 md:py-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="kicker">Philosophy</div>
            <h2 className="mt-6 font-display text-4xl md:text-6xl leading-[1.02]">
              Quiet rooms.<br/>
              <span className="italic font-cormorant text-oxblood">Quieter outcomes.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 text-lg md:text-xl font-light text-slate2 leading-relaxed space-y-6">
            <p className="dropcap">
              Lone Star &#9733; Counsel is a boutique Texas law firm in the tradition of the
              trusted family adviser. We serve a deliberate number of clients, giving complex
              decisions the time, care, and precision they deserve. Our senior attorney
              personally reviews and remains actively involved in every matter.
            </p>
            <p>
              Our clients value discretion. Families and business owners return to us over the
              years because they trust us to understand what they have built, protect what
              matters, and give candid advice when it counts.
            </p>
            <div className="pt-4">
              <Link to="/intake" className="btn-primary" data-testid="about-philosophy-cta">
                Request a Conference
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Four principles */}
      <section className="relative bg-ivory hairline-bottom py-24 md:py-32 overflow-hidden" data-testid="principles-section">
        <StarConstellation count={18} seed={17} maxOpacity={0.08} />
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="kicker">Four Principles</div>
          <h2 className="mt-6 font-display text-4xl md:text-6xl leading-[1.02] max-w-3xl">
            How we keep <span className="italic font-cormorant text-oxblood">our promises.</span>
          </h2>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 md:divide-x divide-brass/30 border-t-0">
            {PRINCIPLES.map((p, i) => (
              <div key={i} className="py-10 md:px-10 md:py-4 first:md:pl-0 last:md:pr-0 border-b md:border-b-0 border-transparent last:border-b-0" data-testid={`principle-${i}`}>
                <div className="font-serif italic text-7xl text-brass leading-none">{p.no}</div>
                <h3 className="mt-8 font-serif text-3xl">{p.title}</h3>
                <p className="mt-4 text-slate2 font-light leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Founder — merged from Counsel */}
      <section className="bg-ivory border-t-0 py-24 md:py-32" data-testid="founder-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="kicker">The Founder</div>
          <h2 className="mt-6 font-display text-5xl md:text-7xl leading-[0.95] max-w-3xl">
            One counsel,<br/>
            <span className="italic font-cormorant text-oxblood">two licenses, two states.</span>
          </h2>

          {ATTORNEYS.map((a, idx) => (
            <article key={idx} data-testid={`attorney-${idx}`} className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
              <div className="lg:col-span-4">
                <div className="image-frame aspect-[3/4] max-w-[300px] md:max-w-[340px]">
                  <img src={a.photo || TEAM_PORTRAITS[a.image]} alt={a.name} className="object-cover w-full h-full" />
                </div>
                <div className="mt-4 text-xs uppercase tracking-[0.24em] text-slate2/70 max-w-[340px]">
                  Plate {idx + 1} · {a.role}
                </div>
              </div>
              <div className="lg:col-span-7 lg:col-start-6">
                <div className="kicker">{a.role}</div>
                <h3 className="mt-4 font-display text-5xl md:text-6xl leading-[0.98]">
                  {a.name}
                  {a.credentials && (
                    <span className="font-cormorant italic text-3xl md:text-4xl text-brass ml-3 align-baseline">
                      , {a.credentials}
                    </span>
                  )}
                </h3>
                <div className="font-cormorant italic text-2xl text-oxblood mt-3">{a.practice}</div>

                {(a.bioParagraphs || [a.bio]).map((para, i) => (
                  <p key={i} className={`text-lg text-slate2 leading-relaxed font-light ${i === 0 ? "mt-8 dropcap" : "mt-6"}`}>
                    {para}
                  </p>
                ))}

                <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8 hairline pt-8">
                  <div>
                    <div className="kicker">Education</div>
                    <ul className="mt-3 space-y-1 text-slate2 font-light">
                      {a.education.map((e, i) => (
                        <li key={i} className="font-cormorant italic">{e}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="kicker">Bar Admissions</div>
                    <ul className="mt-3 space-y-1 text-slate2 font-cormorant italic">
                      {a.admissions.map((adm, i) => (
                        <li key={i}>{adm}</li>
                      ))}
                    </ul>
                    {a.cpaStates && (
                      <>
                        <div className="kicker mt-6">CPA Licensure</div>
                        <ul className="mt-3 space-y-1 text-slate2 font-cormorant italic">
                          {a.cpaStates.map((s, i) => (
                            <li key={i}>{s}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                </div>

                <div className="mt-10">
                  <Link to="/intake" className="btn-primary" data-testid={`attorney-cta-${idx}`}>
                    Request a Conference →
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Of Counsel — discreet, firm-forward. Signals depth without listing names. */}
      <section className="bg-linen py-16 md:py-20 border-t-0" data-testid="of-counsel-section">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-4">
              <div className="kicker">Of Counsel</div>
              <h2 className="mt-4 font-display text-3xl md:text-4xl leading-[1.05] text-navy">
                Engaged on <span className="italic font-cormorant text-oxblood">request</span>.
              </h2>
            </div>
            <div className="lg:col-span-7 lg:col-start-6 space-y-5 text-base md:text-lg font-light text-slate2 leading-relaxed">
              <p>
                The firm maintains standing relationships with a curated roster of Of Counsel
                attorneys — each a seasoned specialist in matters requiring particular depth, from
                complex Chapter 11 litigation and cross-border transactions to niche tax controversies
                and out-of-state probate.
              </p>
              <p>
                These affiliates are engaged discreetly and only when a matter benefits from their
                singular expertise. Names, jurisdictions, and credentials are furnished to prospective
                clients on request.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values — three principles */}
      <section className="bg-ivory py-24 md:py-32" data-testid="founder-values">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <div className="kicker">Values</div>
            <h2 className="mt-6 font-display text-5xl md:text-6xl leading-[0.98]">
              Three principles<br/>
              <span className="italic font-cormorant text-oxblood">governing every engagement.</span>
            </h2>
          </div>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-12">
            {FOUNDER_VALUES.map((v, i) => (
              <article key={v.no} className="hairline-top pt-8" data-testid={`value-${i}`}>
                <div className="font-cormorant italic text-6xl text-brass leading-none">{v.no}</div>
                <h3 className="mt-6 font-display text-2xl md:text-3xl text-navy leading-[1.1]">{v.title}</h3>
                <p className="mt-4 text-base md:text-lg text-slate2 font-light leading-relaxed">{v.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Manifesto — signed personal statement */}
      <OfficeGallery />

      <section className="panel-navy py-28 md:py-40" data-testid="founder-manifesto">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="kicker kicker-ivory">{FOUNDER_MANIFESTO.kicker}</div>
              <h2 className="mt-6 font-display text-4xl md:text-5xl leading-[1.0] text-ivory">
                {FOUNDER_MANIFESTO.headline}
              </h2>
              <p className="mt-6 font-cormorant italic text-2xl text-brass leading-snug">
                {FOUNDER_MANIFESTO.italic}
              </p>
              <div className="mt-10 image-frame aspect-[3/2] max-w-[420px]">
                <img
                  src={FOUNDER_MANIFESTO.environmentalPhoto || ATTORNEYS[0].photo}
                  alt="Darryl V. Pratt at his Frisco office"
                  className="object-cover w-full h-full"
                />
              </div>
              <div className="mt-3 text-[10px] uppercase tracking-[0.28em] text-brass/70 font-medium">
                Plate 2 · At the Frisco Office
              </div>
            </div>
            <div className="lg:col-span-7 lg:col-start-6 space-y-6 text-lg md:text-xl text-ivory/85 font-light leading-relaxed">
              {FOUNDER_MANIFESTO.paragraphs.map((p, i) => (
                <p key={i} className={i === 0 ? "font-cormorant italic text-2xl md:text-3xl text-ivory leading-snug" : ""} data-testid={`manifesto-p-${i}`}>
                  {p}
                </p>
              ))}
              <div className="pt-8 hairline-top">
                <div className="font-cormorant italic text-3xl text-brass" data-testid="manifesto-signature">
                  {FOUNDER_MANIFESTO.signature}
                </div>
                <div className="mt-1 text-[11px] uppercase tracking-[0.28em] text-ivory/60 font-medium">
                  {FOUNDER_MANIFESTO.signatureRole}
                </div>
                <Link
                  to="/attorneys/darryl-v-pratt"
                  className="mt-6 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-brass hover:text-ivory transition-colors font-medium border-b border-brass/40 hover:border-ivory pb-1"
                  data-testid="manifesto-bio-link"
                >
                  Full Attorney Bio &rarr;
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* People First — the personal note */}
      <section className="bg-linen py-24 md:py-32 hairline-top" data-testid="people-first-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <div className="kicker">People First</div>
              <h2 className="mt-6 font-display text-4xl md:text-5xl leading-[1.05] text-navy" data-testid="people-first-headline">
                Why I do this{" "}
                <span className="italic text-oxblood">work.</span>
              </h2>
              <p className="mt-8 font-cormorant italic text-2xl md:text-3xl text-navy/80 leading-snug" data-testid="people-first-caption">
                Counsel is personal. Every business, family, and legacy deserves thoughtful guidance from someone who listens.
              </p>
              <p className="mt-8 text-lg text-navy/75 leading-relaxed">
                A boutique Texas firm advising on business law, estate planning, probate and trust administration, asset protection, and business succession. Darryl oversees every matter — not a rotation of associates, not a service tier, not a portal ticket. Faith, family, clients, community: in that order, and steadily, for the ones we serve.
              </p>
            </div>
            <div className="lg:col-span-6 order-1 lg:order-2">
              <div className="image-frame aspect-[4/3] max-w-[720px] mx-auto lg:mx-0 lg:ml-auto" data-testid="people-first-image-frame">
                <img
                  src="/img/darryl-murphie.jpg"
                  alt="Darryl V. Pratt, Esq., CPA, Principal and Managing Attorney of Lone Star Counsel, seated at home with his golden retriever Murphie."
                  className="object-cover w-full h-full"
                  loading="lazy"
                  decoding="async"
                  width="1312"
                  height="1199"
                  data-testid="people-first-image"
                />
              </div>
              <div className="mt-3 text-[10px] uppercase tracking-[0.28em] text-brass/70 font-medium text-center lg:text-right">
                At home · with Murphie
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Professional Memberships */}
      <section className="relative bg-ivory py-24 md:py-32 overflow-hidden" data-testid="memberships-section">
        <StarConstellation count={22} seed={83} maxOpacity={0.07} />
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="kicker">Professional Memberships</div>
              <h2 className="mt-6 font-display text-4xl md:text-5xl leading-[1.02]">
                Bar sections.<br/>
                <span className="italic font-cormorant text-oxblood">CPA societies. Boards.</span>
              </h2>
              <p className="mt-8 text-slate2 font-light leading-relaxed">
                Active membership in the bar sections, CPA societies, and professional
                associations that keep the practice current, credentialed, and engaged with
                the profession.
              </p>
            </div>
            <div className="lg:col-span-7 lg:col-start-6 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10">
              {PROFESSIONAL_MEMBERSHIPS.map((m, i) => (
                <div key={m.section} className="hairline-top pt-6" data-testid={`membership-${i}`}>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-brass font-medium">{m.section}</div>
                  <ul className="mt-4 space-y-2">
                    {m.items.map((it, j) => (
                      <li key={j} className="font-cormorant italic text-lg text-slate2 leading-snug flex items-start gap-3">
                        <span className="text-brass mt-2 text-[0.55rem]">◆</span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Professional Accomplishments — Badge strip */}
      <section className="bg-linen py-20 md:py-24 border-y border-brass/25" data-testid="accomplishments-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-4">
              <div className="kicker">Professional Accomplishments</div>
              <h2 className="mt-6 font-display text-4xl md:text-5xl leading-[1.02]">
                Peer-reviewed.<br/>
                <span className="italic font-cormorant text-oxblood">Independently recognized.</span>
              </h2>
              <p className="mt-6 text-slate2 font-light leading-relaxed">
                A selection of the third-party recognitions, peer-reviewed ratings, and accredited-attorney credentials held by the firm.
              </p>
            </div>
            <div className="lg:col-span-8" data-testid="accomplishments-badges">
              <ul className="grid grid-cols-2 md:grid-cols-3 gap-x-10 gap-y-10 items-center">
                {[
                  { src: "/brand/badges/av-preeminent.png",        alt: "AV Preeminent — Martindale-Hubbell peer review rating",                                  label: "AV Preeminent® · Martindale-Hubbell" },
                  { src: "/brand/badges/super-lawyers.png",        alt: "Rated by Super Lawyers",                                                                 label: "Rated by Super Lawyers" },
                  { src: "/brand/badges/best-lawyers.png",         alt: "Best Lawyers",                                                                           label: "Best Lawyers®" },
                  { src: "/brand/badges/best-lawyers-2022.png",    alt: "Best Lawyers in America — 2022 recognition",                                             label: "Best Lawyers in America · 2022" },
                  { src: "/brand/badges/best-law-firms-usnews.png",alt: "Best Law Firms — U.S. News",                                                             label: "Best Law Firms · U.S. News" },
                  { src: "/brand/badges/texas-bar-college.png",    alt: "Fellow of the Texas Bar College — a professional society of legal scholars",             label: "Fellow · Texas Bar College" },
                  { src: "/brand/badges/naela.png",                alt: "NAELA — National Academy of Elder Law Attorneys",                                        label: "NAELA · Elder Law Attorneys" },
                  { src: "/brand/badges/attorney-cpas.png",        alt: "Member — American Academy of Attorney-CPAs",                                             label: "Member · American Academy of Attorney-CPAs" },
                  { src: "/brand/badges/va-accredited.png",        alt: "U.S. Department of Veterans Affairs — Accredited Attorney",                              label: "VA Accredited Attorney" },
                ].map((b, i) => (
                  <li key={i} className="flex flex-col items-center text-center" data-testid={`accomplishment-badge-${i}`}>
                    <div className="h-24 md:h-28 flex items-center justify-center px-3">
                      <img src={b.src} alt={b.alt} loading="lazy" decoding="async" className="max-h-full max-w-full object-contain" />
                    </div>
                    <div className="mt-4 text-[10px] uppercase tracking-[0.24em] text-navy/80 font-medium leading-snug">{b.label}</div>
                  </li>
                ))}
              </ul>
              <p className="mt-10 text-xs text-slate2/80 font-cormorant italic leading-relaxed max-w-3xl">
                Badges are the trademarks of their respective organizations and are displayed to identify the firm's third-party recognitions and accreditations. No aspect of these advertisements has been approved by the Supreme Court of Texas. The firm's full recognition methodology and third-party disclosure notices remain a forthcoming content item.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Speaking & Published Work */}
      <section className="bg-linen py-24 md:py-32" data-testid="speaking-published">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="max-w-3xl mb-14">
            <div className="kicker">Speaking &amp; Published Work</div>
            <h2 className="mt-6 font-display text-4xl md:text-5xl leading-[1.02]">
              Contributions to<br/>
              <span className="italic font-cormorant text-oxblood">the profession.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {SPEAKING_PUBLISHED.map((group, i) => (
              <div key={group.kind} data-testid={`speaking-${i}`}>
                <div className="text-[10px] uppercase tracking-[0.28em] text-brass font-medium">{group.kind}</div>
                <ul className="mt-6 space-y-5">
                  {group.items.map((it, j) => (
                    <li key={j} className="hairline-top pt-4 font-cormorant text-lg md:text-xl text-navy leading-snug italic" data-testid={`speaking-item-${i}-${j}`}>
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Press & Recognition */}
      <section className="bg-ivory py-24 md:py-32" data-testid="press-media">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-baseline justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <div className="kicker">Press &amp; Recognition</div>
              <h2 className="mt-6 font-display text-4xl md:text-5xl leading-[1.02]">
                Cited counsel,<br/>
                <span className="italic font-cormorant text-oxblood">quietly noted.</span>
              </h2>
            </div>
            <p className="md:max-w-sm font-cormorant italic text-slate2 text-lg leading-snug">
              Media inquiries and speaking requests may be directed to the firm.
            </p>
          </div>

          <PressStrip
            className="mb-20"
            publications={Array.from(new Set(PRESS_MEDIA.map((p) => p.publication)))}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-14">
            {PRESS_MEDIA.map((p, i) => (
              <article key={i} className="hairline-top pt-8" data-testid={`press-${i}`}>
                <div className="flex items-baseline justify-between gap-4">
                  <div className="font-cormorant italic text-2xl text-brass">{p.publication}</div>
                  {p.date && (
                    <div className="text-[10px] uppercase tracking-[0.32em] text-slate2/60 font-medium shrink-0">
                      {p.date}
                    </div>
                  )}
                </div>
                <div className="mt-2 text-[10px] uppercase tracking-[0.28em] text-slate2/70 font-medium">
                  {p.kind}
                  {p.reporter && <span className="text-slate2/50"> &middot; {p.reporter}</span>}
                </div>
                <h3 className="mt-5 font-display text-xl md:text-2xl text-navy leading-[1.15]">
                  {p.url ? (
                    <a href={p.url} target="_blank" rel="noreferrer" className="hover:text-brass transition-colors">
                      &ldquo;{p.title}&rdquo;
                    </a>
                  ) : (
                    <>&ldquo;{p.title}&rdquo;</>
                  )}
                </h3>
                {p.excerpt && (
                  <p className="mt-4 text-sm font-cormorant italic text-slate2 leading-relaxed">
                    {p.excerpt}
                  </p>
                )}
                {p.note && (
                  <div className="mt-4 text-xs font-cormorant italic text-slate2/70 leading-snug">
                    {p.note}
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden" data-testid="about-cta">
        <div className="absolute inset-0">
          <img src={OAK_IMAGE} alt="Oak" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-navy/75"></div>
        </div>
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-36 text-ivory">
          <h2 className="font-display text-5xl md:text-7xl leading-[0.95] max-w-3xl">
            When you are ready,<br/>
            <span className="italic font-cormorant text-brass">we are here.</span>
          </h2>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/intake" className="btn-brass" data-testid="about-cta-intake">Request a Conference</Link>
            <Link to="/contact" className="btn-ghost !border-ivory !text-ivory hover:!bg-ivory hover:!text-navy" data-testid="about-cta-contact">Write to the Firm</Link>
          </div>
          <div className="max-w-3xl">
            <AttorneyReviewNote reviewed="February 2026" variant="dark" />
          </div>
        </div>
      </section>
    </div>
  );
}
